import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';

import { Condition, ConditionGroup } from 'editor/src/store/design/types';
import { RootState } from 'editor/src/store/index';

export interface GlobalConditionFromOtherSpread {
  spreadIndex: number;
  /** The owning spread's name, so a panel can tell the author where the
   *  condition and its linked elements are stored. */
  spreadName: string;
  conditionGroup: ConditionGroup;
  condition: Condition;
}

// Almost every design has no design-scoped picklist at all. Returning one
// shared array for that case keeps the selector referentially stable, so the
// `shallowEqual` in the panels that call it actually holds — a fresh `[]` per
// call would re-render them on every dispatched action, mousemove included.
const NONE: GlobalConditionFromOtherSpread[] = [];

const selectSpreads = (state: RootState) => state.design.designData?.spreads;
const selectCurrentSpreadIndex = (_state: RootState, currentSpreadIndex: number) => currentSpreadIndex;

// `spreads` changes on every element edit, so the selector recomputes on every
// keystroke — and unlike getDesignConditions, whose members ARE the condition
// objects immer left alone, the entries here are fresh wrappers that no
// reference check can see through. Comparing them field by field lets the last
// result be handed back whenever nothing about the borrowed picklists moved,
// which is what the `shallowEqual` in the panels relies on.
const sameEntries = (a: GlobalConditionFromOtherSpread[], b: GlobalConditionFromOtherSpread[]) =>
  a.length === b.length && a.every((entry, index) => shallowEqual(entry, b[index]));

// The last non-empty result, kept so an unrelated design edit can return it
// unchanged. createSelector's own memo only covers repeated calls on the SAME
// state; this covers the recompute. A miss just yields a fresh array.
let previous: GlobalConditionFromOtherSpread[] = NONE;

/**
 * A condition is STORED on exactly one spread. `showOnAllSpreads` asks for its
 * picklist on EVERY page, so a page that does not store it has to render it
 * itself — and that is what this returns: the design-scoped conditions owned by
 * OTHER spreads, each paired with the spread that owns it.
 *
 * The owning spread renders its own through its group, so it is skipped here to
 * avoid a double. Top-level only: `addConditionToConditionReducer` moves a
 * nested condition out of the `top` bucket into its parent's option bucket, so
 * `!condition.parent` is exactly "still shown in its own right". A nested
 * condition is shown by its parent when that option is active — including on a
 * page that borrows the parent, see `optionsOnly` in PersonalizationCondition.
 */
const getGlobalConditionsFromOtherSpreads = createSelector(
  [selectSpreads, selectCurrentSpreadIndex],
  (spreads, currentSpreadIndex): GlobalConditionFromOtherSpread[] => {
    const globalConditions = (spreads ?? []).flatMap((spread, spreadIndex) => {
      if (spreadIndex === currentSpreadIndex || !spread.conditionGroup) {
        return [];
      }
      const { conditionGroup } = spread;
      return Object.values(conditionGroup.conditions)
        .filter((condition) => condition.showOnAllSpreads && !condition.parent)
        .map((condition) => ({ spreadIndex, spreadName: spread.name, conditionGroup, condition }));
    });

    if (!globalConditions.length) {
      return NONE;
    }
    if (sameEntries(globalConditions, previous)) {
      return previous;
    }
    previous = globalConditions;
    return globalConditions;
  },
);

export default getGlobalConditionsFromOtherSpreads;
